import React from 'react';
import { computed, reaction, action } from 'mobx';

import { Variables } from 'core/query';
import { clientStore } from 'apps/client/app';
import { PaginationOffsetManager } from 'libs/connection';
import { ValueInputType } from 'libs/connection/actions/filterModels';
import { PortalLink } from 'apps/client/components/PortalLink';
import { formatPrice } from 'utils/formatStrings';
import { Paragraph } from 'components';
import { DateWithTooltip } from 'apps/components';

import {
  DateCell,
  makeEnumCell,
  RfqNameLink,
  makeDecimalIdLink,
  CadPreview,
  Space,
} from 'libs/connection/cells';
import {
  DateFilter,
  EnumFilter,
  SpaceFilter,
} from 'libs/connection/actions/filters';
import { graphqlIdToDecimal } from 'utils/dgraphId';

import Icon from 'components/Icon';
import colors from 'styles/themes/wikifactory/styles/colors';

import { analytics } from 'core/analytics';

import {
  ProductQuotePreviewCell,
  ProductQuoteBuildDetailsCell,
} from 'apps/productQuote/cells';

import { ToggleButton } from 'libs/connection/Table/ToggleButton';

import orderStatuses from 'apps/order/orderStatuses';

import { OrderModel } from '../app';
import {
  OrderConnectionQuery,
  IntermediatorOrderConnectionQuery,
} from '../gql';

export const PaymentStatusEnum = {
  INITIALIZED: {
    // (What does this mean) payment status has been initialized
    title: 'Initialized',
    color: colors.wfYellow,
  },
  PAIDPARTIAL: {
    //  part of the payment has been made
    title: 'Partially Paid',
    color: colors.wfOrange,
  },
  // TODO: Remove once keys are consistent.
  PAIDFULL: {
    title: 'Fully Paid',
    color: colors.wfGreen,
  },
  REFUNDED: {
    // the order has been refunded
    title: 'Refunded',
    color: colors.wfRed,
  },
};

const customerSpaceCol = {
  Header: 'Customer',
  accessor: 'quote.customerSpace',
  Cell: Space,
  filter: SpaceFilter,
  fieldType: ValueInputType.STRING,
  filterParamName: 'customerSpaceID',
};
const onOrderCellClicked = () => {
  analytics.sendEvent(
    'marketplace-order-dashboard-click-view',
    {},
    { ga: true },
  );
};

export const getColumns = () => [
  {
    Header: 'Preview',
    accessor: '',
    Cell: function Preview(order, c, conn, rowState) {
      if (order?.quote.productQuotes.length === 1) {
        const { file } = order.quote.productQuotes[0].for.build;
        return (
          <PortalLink
            name='order'
            params={{ orderId: graphqlIdToDecimal(order.id) }}
            onClick={onOrderCellClicked}
          >
            {CadPreview(file)}
          </PortalLink>
        );
      }

      return (
        <ToggleButton
          onClick={rowState.onToggleExpand}
          isToggled={rowState.isExpanded}
        />
      );
    },
    SubCell: ProductQuotePreviewCell,
  },
  {
    Header: 'Order Id',
    accessor: '',
    Cell: function quoteLink(order) {
      return makeDecimalIdLink({
        routeName: 'order',
        paramName: 'orderId',
        prefix: 'O',
      })(order.id);
    },
  },
  {
    Header: 'RFQ ID',
    accessor: 'quote',
    Cell: function RfqLink(quote) {
      if (quote.productQuotes.length > 1) {
        return <Paragraph>...</Paragraph>;
      }
      if (quote.productQuotes.length === 1) {
        return <RfqNameLink rfq={quote.productQuotes[0].from} showId />;
      }
      return <Paragraph>-</Paragraph>;
    },
    SubCell: function RfqLinkSubCell(productQuote) {
      return <RfqNameLink rfq={productQuote.from} showId />;
    },
  },
  {
    Header: 'Created Date',
    isSortable: true,
    filter: DateFilter,
    accessor: 'createdAt',
    fieldType: ValueInputType.DATETIME,
    Cell: function CreatedDate(date) {
      return <DateWithTooltip tipId='createdAtCell' date={date} />;
    },
    SubCell: function CreatedDateSubCell(productQuote) {
      return (
        <DateWithTooltip
          tipId='createdAtSubCell'
          date={productQuote.createdAt}
        />
      );
    },
  },
  {
    Header: 'Order Status',
    accessor: 'orderStatus',
    isSortable: true,
    filter: EnumFilter,
    filterArgs: { valuesEnum: orderStatuses },
    Cell: function OrderStatusCell(orderStatus) {
      return <div>{makeEnumCell(orderStatuses)(orderStatus)}</div>;
    },
    fieldType: ValueInputType.STRING,
  },
  // {
  //   Header: 'Payment Status',
  //   accessor: 'paymentStatus',
  //   isSortable: true,
  //   filter: EnumFilter,
  //   filterArgs: { valuesEnum: PaymentStatusEnum },
  //   Cell: makeEnumCell(PaymentStatusEnum),
  // },
  // {
  //   Header: 'Process',
  //   accessor: 'quote.for.build.process.name',
  // },
  // {
  //   Header: 'Material',
  //   accessor: 'quote.for.build.material.name',
  // },
  {
    Header: 'Part Details',
    accessor: 'quote',
    Cell: function PartDetails(quote) {
      if (quote.productQuotes.length === 1) {
        return ProductQuoteBuildDetailsCell(quote.productQuotes[0]);
      }
      return <Paragraph>...</Paragraph>;
    },
    SubCell: ProductQuoteBuildDetailsCell,
  },
  {
    Header: 'Quantity',
    accessor: 'quote',
    Cell: function Quantity(quote) {
      const quantities = quote.productQuotes
        .map(productQuote => productQuote.for.quantity)
        .join(', ');
      return <Paragraph>{quantities}</Paragraph>;
    },
    SubCell: function QuantitySubCell(productQuote) {
      return <Paragraph>{productQuote.for.quantity}</Paragraph>;
    },
  },
  {
    Header: 'Total Price',
    accessor: 'quote',
    Cell: function TotalCost(quote) {
      const { currency } = quote.productQuotes[0];
      return (
        <Paragraph>{formatPrice(quote.totalCost / 100, currency)}</Paragraph>
      );
    },
    SubCell: function TotalCostSubCell(productQuote) {
      const { cost, currency } = productQuote;
      return <Paragraph>{formatPrice(cost / 100, currency)}</Paragraph>;
    },
  },
  {
    Header: 'Delivery Date',
    accessor: 'deliveryDate',
    Cell: DateCell,
  },
  {
    Header: 'Receipt',
    accessor: 'payments',
    isNumeric: true, // for right alignment
    Cell: function receiptUrl(payments) {
      return (
        <a download href={payments[0].receiptUrl}>
          <Icon name='download' />
        </a>
      );
    },
  },
];

export class OrderConnManager extends PaginationOffsetManager {
  static ElementModel = OrderModel;

  static defaultOrder = 'DESC';
  static defaultSort = 'createdAt';

  constructor(parent, init, options) {
    super(parent, init, options);
    this.batchSize = 10;

    this.variables = this.variables.extend(
      new Variables({
        spaceId: () =>
          clientStore.activeSpaceId || clientStore.signedInProfile.inSpace.id,
      }),
    );

    this.variables.onChange(
      ['spaceId'],
      () => {
        this.initiateQuery();
      },
      { fireImmediately: false },
    );

    this.disposers.push(
      reaction(
        () => clientStore.currentPortal,
        () => {
          this.initiateQuery();
        },
        { fireImmediately: true },
      ),
    );
  }

  @computed get finalColumns() {
    const columns = getColumns();
    if (clientStore.currentPortal === 'INTERMEDIATOR') {
      const cols = [...columns];
      cols.splice(2, 0, customerSpaceCol);
      return cols;
    }
    return columns;
  }

  @computed get filterColumns() {
    return this.finalColumns.filter(col => col.filter !== undefined);
  }

  @action initiateQuery = () => {
    this.InitialiseFilters();
    if (clientStore.currentPortal === '') {
      return;
    }
    let query = OrderConnectionQuery;
    if (clientStore.currentPortal === 'INTERMEDIATOR') {
      query = IntermediatorOrderConnectionQuery;
    }
    this.initialiseQuery(query, this.variables, clientStore.roleHeaders);
  };
}
